import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Puzzle, Lock } from "lucide-react";
import Reveal from "@/components/Reveal";
import { EMBLEM_URL } from "@/data/content";
import { GAMES, RELEASES, RUST_PROJECTS } from "@/data/releases";

const ReleaseCard = ({ release, delay }) => {
  const game = GAMES[release.game];
  return (
    <Reveal delay={delay}>
      <Link
        to={release.path || `/projects/${release.slug}`}
        data-testid={`release-card-${release.slug}`}
        className="group flex h-full flex-col border border-[#282D37] bg-[#121418] transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/60 hover:shadow-[0_0_50px_rgba(229,46,46,0.08)]"
      >
        <div className="flex items-center justify-between border-b border-[#282D37] bg-[#0D0F12] px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={release.logo || EMBLEM_URL} alt={`${release.name} emblem`} className="w-8" />
            <span className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">ARSENAL FORGE</span>
          </div>
          <span className="border border-[#E52E2E]/50 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.25em] text-[#E52E2E]">
            {release.status}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6 sm:p-7">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">
            {game.name.toUpperCase()} · {release.category.toUpperCase()}
          </p>
          <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
            {release.name}
          </h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-[#9EA4B0]">{release.summary}</p>
          <div className="mt-6 flex flex-wrap items-center gap-2 font-mono text-[10px] tracking-[0.2em]">
            <span className="border border-[#282D37] px-2.5 py-1 text-[#9EA4B0]">v{release.version}</span>
            {release.pricing && (
              <span className="border border-[#282D37] px-2.5 py-1 text-[#9EA4B0]">{release.pricing}</span>
            )}
            <span className="border border-[#282D37] px-2.5 py-1 text-[#9EA4B0]">{release.client.toUpperCase()}</span>
          </div>
          <span className="mt-6 flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
            {release.cardAction || `VIEW ${game.term.toUpperCase()}`}
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

const RustProjectCard = ({ project, delay }) => (
  <Reveal delay={delay}>
    <div
      data-testid={`rust-project-${project.slug}`}
      className="group h-full border border-[#282D37] bg-[#121418] p-7 transition-[border-color] duration-500 hover:border-[#E52E2E]/50"
    >
      <div className="flex items-start justify-between">
        <Puzzle size={20} className="text-[#E52E2E]" />
        <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.25em] text-[#5F6573]">
          <Lock size={10} /> {project.availability}
        </span>
      </div>
      <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">
        {project.name}
      </h3>
      <p className="mt-1 font-mono text-[9px] tracking-[0.3em] text-[#E52E2E]">{project.domain}</p>
      <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{project.summary}</p>
    </div>
  </Reveal>
);

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const show = (g) => filter === "all" || filter === g;
  const wowReleases = RELEASES.filter((r) => r.game === "wow" && r.listed !== false);
  const filters = [{ id: "all", label: "ALL" }, ...Object.values(GAMES).map((g) => ({ id: g.id, label: g.catalogueLabel }))];

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[480px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">PROJECTS // DOWNLOADS</p>
          <h1 className="mt-5 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
            Forge Projects
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#9EA4B0]">
            Everything we build, in one armoury. Rust plugins running the Arsenal network, and
            World of Warcraft addons — led by the Arsenal Hub guild suite.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="project-filters">
            {filters.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`border px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.25em] transition-colors duration-300 ${
                  filter === f.id
                    ? "border-[#E52E2E] bg-[#E52E2E] text-white"
                    : "border-[#282D37] text-[#9EA4B0] hover:border-[#E52E2E]/60 hover:text-[#F4F5F7]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
      </header>

      <section className="relative mx-auto max-w-[1400px] px-6 py-16 lg:px-10">
        {show("wow") && (
          <div data-testid="section-wow">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">WORLD OF WARCRAFT ADDONS</p>
            </Reveal>
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {wowReleases.map((r, i) => (
                <ReleaseCard key={r.slug} release={r} delay={0.06 * i} />
              ))}
            </div>
          </div>
        )}

        {show("rust") && (
          <div data-testid="section-rust" className={show("wow") ? "mt-20" : ""}>
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">RUST PLUGINS</p>
                <Link
                  to="/plugins"
                  data-testid="rust-foundry-link"
                  className="group flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
                >
                  RELEASE PIPELINE & NETWORK
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {RUST_PROJECTS.map((p, i) => (
                <RustProjectCard key={p.slug} project={p} delay={0.05 * i} />
              ))}
            </div>
            <Reveal delay={0.1}>
              <p className="mt-8 border border-dashed border-[#282D37] px-6 py-5 text-center font-mono text-[10px] tracking-[0.25em] text-[#5F6573]">
                RUST SYSTEMS ARE PROPRIETARY TO THE ARSENAL NETWORK — PUBLIC RELEASES LAND IN THE FOUNDRY MARKETPLACE.
              </p>
            </Reveal>
          </div>
        )}
      </section>
    </div>
  );
};

export default Projects;
